import { Link } from "react-router-dom";
import { img } from "../data/media";

export function InsightsPage() {
  const coastal = img("coastal-marine");
  const carbon = img("insights-carbon");
  const mural = img("biomimicry-mural");
  const field = img("field-ecology");

  return (
    <section className="section">
      <div className="container">
        <div className="prose" style={{ maxWidth: "none" }}>
          <h2>Insights that save programme time</h2>
          <p className="muted">
            Short, practical notes from real jobs — so you avoid the delays we see every week.
          </p>
        </div>
        <div className="grid-2" style={{ marginTop: "1.5rem" }}>
          <article className="card media-card">
            <img src={field.src} alt={field.alt} loading="lazy" />
            <div className="media-card-body">
              <h3>ASS investigation pacing on urban sites</h3>
              <p>
                How to stage sampling intensity against excavation risk without over-scoping early DA
                work.
              </p>
            </div>
          </article>
          <article className="card media-card">
            <img src={img("crew-clearing").src} alt={img("crew-clearing").alt} loading="lazy" />
            <div className="media-card-body">
              <h3>ESC that survives the first storm</h3>
              <p>
                Design checks that catch undersized controls before construction commits to the wrong
                sequence.
              </p>
            </div>
          </article>
          <article className="card media-card">
            <img src={coastal.src} alt={coastal.alt} loading="lazy" />
            <div className="media-card-body">
              <h3>Blue carbon project framing</h3>
              <p>
                A concise canvas for carbon pools, stakeholders and monitoring before deep feasibility
                spend.{" "}
                <Link to="/resources/blue-carbon-project-canvas">Download the free canvas</Link>.
              </p>
            </div>
          </article>
          <article className="card media-card">
            <img src={carbon.src} alt={carbon.alt} loading="lazy" />
            <div className="media-card-body">
              <h3>Approvals strategy as a design input</h3>
              <p>
                Treat trigger mapping as an early design constraint — not a late compliance scramble.
              </p>
            </div>
          </article>
        </div>
        <figure className="card media-card" style={{ marginTop: "1.25rem" }}>
          <img src={mural.src} alt={mural.alt} loading="lazy" />
          <div className="media-card-body">
            <p className="muted" style={{ margin: 0 }}>
              {mural.caption} — biomimicry-inspired thinking sits alongside engineering method.
            </p>
          </div>
        </figure>
      </div>
    </section>
  );
}

export function TrackRecordPage() {
  const tiles = [
    {
      title: "Coastal & marine",
      body: "Marine plants, intertidal systems and blue carbon framing for coastal projects.",
      image: img("coastal-marine"),
    },
    {
      title: "Urban & subdivision",
      body: "Lot-scale packages, soils, WSUD interfaces and approvals sequencing for developers.",
      image: img("infra-chamber"),
    },
    {
      title: "Construction control",
      body: "ESC/CPESC support that connects plan intent to practical site implementation.",
      image: img("crew-clearing"),
    },
    {
      title: "Field ecology",
      body: "Site evidence, vegetation and waterway interfaces documented in the field.",
      image: img("field-ecology"),
    },
    {
      title: "Materials & circular pathways",
      body: "Soils, reuse streams and materials handling as part of project delivery.",
      image: img("materials-glass"),
    },
    {
      title: "Architecture & heritage context",
      body: "Environmental input where design and heritage settings shape project pathways.",
      image: img("architecture-brutalist"),
    },
  ];

  return (
    <section className="section">
      <div className="container">
        <h2>Work that looks like yours</h2>
        <p className="muted" style={{ maxWidth: "60ch" }}>
          Coastal, urban, construction and soils — delivered for clients who need defensible
          environmental decisions from Ecological Engineering.
        </p>
        <div className="track-record-grid">
          {tiles.map((t) => (
            <article key={t.title} className="card media-card">
              <img src={t.image.src} alt={t.image.alt} loading="lazy" />
              <div className="media-card-body">
                <h3>{t.title}</h3>
                <p>{t.body}</p>
              </div>
            </article>
          ))}
        </div>
        <p style={{ marginTop: "1.5rem" }}>
          <Link className="btn btn-primary" to="/contact">
            Get help on a similar project
          </Link>
        </p>
      </div>
    </section>
  );
}

export function PrivacyPage() {
  return (
    <section className="section">
      <div className="container prose">
        <h2>Privacy Policy</h2>
        <p className="muted">Ecological Engineering · EcoEng.net · Last updated August 2026</p>
        <p>
          We collect account details, project information you submit, uploaded files, and payment
          metadata required to deliver DIY resources, guided reports and consultancy services.
        </p>
        <h3>What we collect</h3>
        <ul>
          <li>Identity and contact data (name, email, company, phone, ABN)</li>
          <li>Project questionnaire answers and uploaded documents</li>
          <li>Payment records via Stripe (we do not store full card numbers)</li>
          <li>Technical logs required for security and audit</li>
        </ul>
        <h3>How we use data</h3>
        <ul>
          <li>To provide downloads, reports and consultancy services</li>
          <li>To process payments and issue tax invoices</li>
          <li>To communicate about orders, submissions and account security</li>
        </ul>
        <h3>Storage</h3>
        <p>
          Application data is stored in Cloudflare D1; files in Cloudflare R2. Access is restricted
          by authentication and role-based controls.
        </p>
        <h3>Contact</h3>
        <p>
          Privacy enquiries: use the <Link to="/contact">contact form</Link> or email the address
          published on EcoEng.net.
        </p>
      </div>
    </section>
  );
}

export function TermsPage() {
  return (
    <section className="section">
      <div className="container prose">
        <h2>Terms of Service</h2>
        <p className="muted">Ecological Engineering · EcoEng.net</p>
        <h3>Platform use</h3>
        <p>
          EcoEng.net provides digital resources, guided report workflows and enquiry channels. You
          must provide accurate information and keep account credentials secure.
        </p>
        <h3>Purchases</h3>
        <p>
          Prices are shown in AUD. GST is calculated at checkout where applicable. Digital resource
          purchases are generally non-refundable once downloaded, except where required by
          Australian Consumer Law.
        </p>
        <h3>Guided reports</h3>
        <p>
          Tier 2 deliverables depend on the completeness of your inputs and successful payment.
          Target turnaround (~48 hours) is indicative, not a guaranteed SLA for every project.
        </p>
        <h3>Professional services</h3>
        <p>
          Consultancy engagements may require a separate proposal or agreement. Platform terms do
          not replace project-specific contracts.
        </p>
      </div>
    </section>
  );
}

export function ProfessionalResponsibilityPage() {
  return (
    <section className="section">
      <div className="container prose">
        <h2>Professional responsibility & limitations</h2>
        <p>
          Ecological Engineering provides professional environmental engineering and science
          services. Automated and semi-automated tools on this platform are designed to accelerate
          data capture and document production — they do not remove the need for professional
          judgement.
        </p>
        <h3>DIY resources</h3>
        <p>
          Templates and checklists are general practice aids. They must be adapted to site
          conditions, jurisdiction and project risk by a suitably qualified person.
        </p>
        <h3>Guided reports (Do It With You)</h3>
        <p>
          Outputs are based on information you provide and a team review step. They are not a
          substitute for detailed investigation where data gaps, high risk or regulatory
          complexity require it. We may request additional information or recommend escalation to
          full consultancy.
        </p>
        <h3>Liability</h3>
        <p>
          Nothing on this platform excludes rights under Australian Consumer Law. Beyond those
          non-excludable rights, use of DIY materials is at your professional risk, and guided
          report reliance should match the agreed scope of the deliverable.
        </p>
        <p>
          <Link to="/contact">Contact us</Link> if you are unsure which tier is appropriate.
        </p>
      </div>
    </section>
  );
}

export function NotFoundPage() {
  return (
    <section className="section">
      <div className="container">
        <h2>Page not found</h2>
        <p className="muted">That route does not exist on EcoEng.net.</p>
        <Link className="btn btn-primary" to="/">
          Home
        </Link>
      </div>
    </section>
  );
}
